import { getDecoded } from '@/lib/auth';
import { getPouchDB, resetLocalDbHandle } from '@/lib/couch';

export interface BaseDoc {
  _id?: string;
  _rev?: string;
  type: string;
  tenant_id?: string;
  // epoch-ms write stamp; drives last-write-wins conflict resolution
  updatedAt?: number;
}

function newId(type: string): string {
  return `${type}:${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Legacy-migrated docs carry an inner `id` that already includes the
// `<type>:` prefix (the ETL emitted id === _id). Composing `_id` as
// `${type}:${id}` would double the prefix and fork a second doc, which
// then shows up as a duplicate row after hydration.
export function docIdPart(type: string, id: string): string {
  const prefix = `${type}:`;
  return id.startsWith(prefix) ? id.slice(prefix.length) : id;
}

export async function allByType<T extends BaseDoc>(type: string): Promise<T[]> {
  const db = getPouchDB();
  const res = await db.allDocs<T>({
    include_docs: true,
    startkey: `${type}:`,
    endkey: `${type}:￰`,
  });
  return res.rows
    .map(r => r.doc as T | undefined)
    .filter((d): d is T => Boolean(d) && d!.type === type);
}

export async function put<T extends BaseDoc>(doc: T): Promise<T> {
  const decoded = getDecoded();
  const tenantId = doc.tenant_id ?? decoded?.tenant_id;
  const next: T = {
    ...doc,
    _id: doc._id ?? newId(doc.type),
    tenant_id: tenantId,
    updatedAt: Date.now(),
  };
  // Domain shadow-puts (apps/frontend/src/store/index.ts) do not track _rev
  // in the zustand state. To make repeat writes to the same _id idempotent
  // (e.g. add extinguisher → update customer doc), fetch the existing rev
  // and retry once on a 409 conflict.
  const onePut = async (): Promise<T> => {
    const db = getPouchDB();
    try {
      const res = await db.put(next);
      return { ...next, _rev: res.rev };
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status !== 409 || !next._id) throw err;
      const existing = await db.get(next._id);
      const withRev: T = { ...next, _rev: existing._rev };
      const res = await db.put(withRev);
      return { ...withRev, _rev: res.rev };
    }
  };
  try {
    return await onePut();
  } catch (err) {
    // A destroy/recreate of the local DB (tenant switch, teardown) can race an
    // in-flight write → "InvalidStateError: database connection is closing".
    // The on-disk data is intact; drop the stale handle and retry once on a
    // fresh connection so the write persists instead of looping in the queue.
    const name = (err as { name?: string }).name;
    const msg = String((err as { message?: string }).message ?? '');
    if (name === 'InvalidStateError' || msg.includes('connection is closing')) {
      resetLocalDbHandle();
      return await onePut();
    }
    throw err;
  }
}

export async function remove(doc: BaseDoc): Promise<void> {
  const db = getPouchDB();
  if (!doc._id || !doc._rev) return;
  await db.remove(doc._id, doc._rev);
}

// Delete a doc by its `<type>:<idPart>` _id. The zustand state does not track
// _rev, so fetch the live doc for its rev before removing. A missing doc (404)
// is treated as success — the delete already happened or never replicated.
export async function removeByTypeId(
  type: string,
  idPart: string
): Promise<void> {
  const db = getPouchDB();
  const id = `${type}:${idPart}`;
  try {
    const existing = await db.get(id);
    await db.remove(existing._id, existing._rev);
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404) return;
    throw err;
  }
}

// Legacy-migrated spare_part docs are keyed `spare_part:legacy-N`, NOT by their
// articleNr (LEG-000). Keying writes/deletes off the articleNr therefore targets
// a non-existent `spare_part:<articleNr>` doc → 404 no-op (delete silently
// fails, edits fork a duplicate). Resolve the real doc by matching articleNr.

/** Delete every spare_part doc whose articleNr matches (handles legacy keys). */
export async function removeSparePartByArticleNr(
  articleNr: string
): Promise<void> {
  const db = getPouchDB();
  const all = await allByType<BaseDoc & { articleNr?: string }>('spare_part');
  await Promise.all(
    all
      .filter(d => d.articleNr === articleNr)
      .map(async d => {
        if (!d._id || !d._rev) return;
        try {
          await db.remove(d._id, d._rev);
        } catch (err) {
          if ((err as { status?: number }).status !== 404) throw err;
        }
      })
  );
}

/** Upsert a spare_part: update the existing doc in place (by articleNr, whatever
 * its legacy _id), else create one keyed by articleNr. */
export async function putSparePartByArticleNr(
  fields: Record<string, unknown> & { articleNr: string }
): Promise<void> {
  const all = await allByType<BaseDoc & { articleNr?: string }>('spare_part');
  const existing = all.find(d => d.articleNr === fields.articleNr);
  const _id = existing?._id ?? `spare_part:${fields.articleNr}`;
  await put({ ...fields, type: 'spare_part', _id } as never);
}

export type ChangeHandler = (
  change: PouchDB.Core.ChangesResponseChange<BaseDoc>
) => void;

export function changes(types: string[], onChange: ChangeHandler): () => void {
  const db = getPouchDB();
  const feed = db
    .changes<BaseDoc>({ since: 'now', live: true, include_docs: true })
    .on('change', c => {
      const t = c.doc?.type;
      if (!t || types.includes(t)) onChange(c);
    });
  return () => {
    try {
      feed.cancel();
    } catch {
      // ignore
    }
  };
}
